import { Component, ElementRef, EventEmitter, HostListener, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { EventRecurrence, UniteDeTempsEnum } from 'src/app/utils/interfaces/new/event-recurrence.interface';
import * as moment from 'moment';
import { TypeEnum } from 'src/app/utils/interfaces/new/ParametresMois';
import { Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-event-recurrence',
  templateUrl: './event-recurrence.component.html',
  styleUrls: [
    './event-recurrence.component.scss',
    '../../../../fragments/modal/modal.fragment.scss'
  ]
})
export class EventRecurrenceComponent implements OnInit, OnDestroy {

  @ViewChild('modalBody') public modalBody: ElementRef;
  @ViewChild('outsideModal') public outsideModal: ElementRef;

  public recurrence: EventRecurrence | null = null;
  public eventDate = new Date();
  public formGroup: FormGroup;
  public unitArray = ['semaine(s)', 'moi(s)', 'ans'];
  public daysInWeek = [
    { label: 'L', value: 0 },
    { label: 'M', value: 1 },
    { label: 'M', value: 2 },
    { label: 'J', value: 3 },
    { label: 'V', value: 4 },
    { label: 'S', value: 5 },
    { label: 'D', value: 6 }
  ];
  public unitMonthSelect = [];
  public unitMonthSelectLabel: Array<string> = [];

  private valueChangeSub: Subscription;
  private offsetChangeSubscribe: Subscription;
  private endOcurrenceNumberChangeSubscribe: Subscription;

  @Output() public closeEmitter = new EventEmitter();

  @HostListener('document:mousedown', ['$event'])
  onGlobalClick(event): void {
    if (!environment.closeModalOnClickOutside) {
      return;
    }

    if (!this.modalBody.nativeElement.contains(event.target) && this.outsideModal.nativeElement.contains(event.target)) {
        this.closeModal(null);
    }
  }

  constructor(
    private formBuilder: FormBuilder
  ) { }

  ngOnInit(): void {
    let unit = 'semaine(s)';
    let dayOfWeek: Array<number> = [];
    if (this.recurrence) {
      switch (this.recurrence.uniteDeTemps) {
        case 'week':
          unit = 'semaine(s)';
          break;
        case 'month':
          unit = 'moi(s)';
          break;
        case 'year':
          unit = 'ans';
          break
      }

      if (this.recurrence && this.recurrence.parametresSemaine && this.recurrence.parametresSemaine.uniteSemaineJoursDeLaSemaine) {
        for (const dow of this.recurrence.parametresSemaine.uniteSemaineJoursDeLaSemaine) {
          dayOfWeek.push(dow);
        }
      }
    }

    this.__generatMonthSelect();

    const montUnitLabel = this.__getMontUnitLabel();

    this.formGroup = this.formBuilder.group({
      offset: [this.recurrence ? this.recurrence.delais : 0],
      unit: [unit],
      dayOfWeek: [dayOfWeek],
      monthUnitLabel: [montUnitLabel],
      endType: [this.recurrence ? this.recurrence.finRecurrence.type : 'never'],
      endDate: [this.recurrence && this.recurrence.finRecurrence.date ? moment(this.recurrence.finRecurrence.date).format('yyyy-MM-DD') : moment(new Date()).format('yyyy-MM-DD')],
      endOcurrenceNumber: [this.recurrence && this.recurrence.finRecurrence.nombreOccurrence ? this.recurrence.finRecurrence.nombreOccurrence : 1],
      keepPatient: [this.recurrence && this.recurrence.keepPatient ? true : false],
      keepDescription: [this.recurrence && this.recurrence.keepDescription ? true : false]
    });

    this.offsetChangeSubscribe = this.formGroup.get('offset').valueChanges.subscribe((v) => {
      if (v && String(v).includes('-')) {
        this.formGroup.get('offset').patchValue(1);
      }
    });

    this.endOcurrenceNumberChangeSubscribe = this.formGroup.get('endOcurrenceNumber').valueChanges.subscribe((v) => {
      if (v && String(v).includes('-')) {
        this.formGroup.get('endOcurrenceNumber').patchValue(1);
      }
    });

    if (this.formGroup.get('unit').value === 'semaine(s)') {
      this.formGroup.get('dayOfWeek').setValidators([Validators.required]);
    }

    this.valueChangeSub = this.formGroup.get('unit').valueChanges.subscribe((v) => {
      if (v === 'semaine(s)') {
        this.formGroup.get('dayOfWeek').setValidators([Validators.required]);
        this.formGroup.get('dayOfWeek').patchValue([]);
        this.formGroup.get('dayOfWeek').updateValueAndValidity();
      } else {
        this.formGroup.get('dayOfWeek').clearValidators();
        this.formGroup.get('dayOfWeek').updateValueAndValidity();
      }
    });
  }

  ngOnDestroy() {
    if (this.valueChangeSub) {
      this.valueChangeSub.unsubscribe();
    }

    if (this.offsetChangeSubscribe) {
      this.offsetChangeSubscribe.unsubscribe();
    }

    if (this.endOcurrenceNumberChangeSubscribe) {
      this.endOcurrenceNumberChangeSubscribe.unsubscribe();
    }
  }

  public addDay(day: { label: string, value: string }) {
    const days = this.formGroup.get('dayOfWeek').value;
    days.push(day.value);
    this.formGroup.get('dayOfWeek').patchValue(days);
  }

  public removeDay(day: { label: string, value: string }) {
    const days = this.formGroup.get('dayOfWeek').value;

    const dayIndex = days.indexOf(day.value);

    if (dayIndex > -1) {
      days.splice(dayIndex, 1);
    }

    this.formGroup.get('dayOfWeek').patchValue(days);
  }

  public saveRecurrence(): void {
    let unit = 'week';
    let monthPropertiesType = 'day_of_month';
    let positionInMonth = 1;

    switch (this.formGroup.get('unit').value) {
      case 'moi(s)':
        unit = 'month';
        break;
      case 'ans':
        unit = 'year';
        break;
    }

    monthPropertiesType = this.__getMontUnitValueWithLabel(this.formGroup.get('monthUnitLabel').value);
    positionInMonth = this.__getDayPositionInMonth(this.formGroup.get('monthUnitLabel').value);

    this.closeModal({
      delais: this.formGroup.get('offset').value,
      dateDebutInitiale: new Date(),
      dateFinInitiale: new Date(),
      id: '',
      uniteDeTemps: unit as UniteDeTempsEnum,
      parametresSemaine: {
        uniteSemaineJoursDeLaSemaine: this.formGroup.get('dayOfWeek').value
      },
      parametresMois: {
        type: monthPropertiesType as TypeEnum,
        positionDansLeMois: positionInMonth
      },
      finRecurrence: {
        type: this.formGroup.get('endType').value,
        date: moment(this.formGroup.get('endDate').value, 'yyyy-MM-DD').toDate(),
        nombreOccurrence: this.formGroup.get('endOcurrenceNumber').value
      },
      keepPatient: this.formGroup.get('keepPatient').value ? true : false,
      keepDescription: this.formGroup.get('keepDescription').value ? true : false
    });
    
  }

  public keepInfos(control: AbstractControl, doKeep: boolean): void {
    control.patchValue(doKeep);
  }

  public closeModal(recurrence: EventRecurrence | null): void {
    this.closeEmitter.emit(recurrence);
  }

  private __generatMonthSelect() {
    moment.locale('fr');
    const monthStart = moment(this.eventDate).startOf('month');
    let previousDate = moment(this.eventDate).clone();
    let i = 0;

    for (i; !monthStart.isAfter(previousDate); ++i) {
      previousDate = previousDate.subtract(1, 'week');
    }

    let label = '';
    if (i === 1) {
      label += '1er';
    } else {
      label += `${i}ème`;
    }

    label += ` ${moment(this.eventDate).format('dddd')} du mois`;

    this.unitMonthSelect.push({
      label,
      value: 'day_position',
      positionInMonth: i
    });

    this.unitMonthSelect.push({
      label: `${moment(this.eventDate).format('DD')} du mois`,
      value: 'day_of_month'
    });

    for (const u of this.unitMonthSelect) {
      this.unitMonthSelectLabel.push(u.label);
    }
  }

  private __getMontUnitLabel() {
    if (this.recurrence && this.recurrence.uniteDeTemps === 'month') {
      return this.unitMonthSelect.find((t) => t.value === this.recurrence.parametresMois.type).label;
    } else {
      return this.unitMonthSelect.find((t) => t.value === 'day_position').label;
    }
  }

  private __getMontUnitValueWithLabel(typeLabel: string) {
    return this.unitMonthSelect.find((t) => t.label === typeLabel).value;
  }

  private __getDayPositionInMonth(monthLabel: string) {
    return this.unitMonthSelect.find((t) => t.label === monthLabel).positionInMonth;
  }

}
